import { memo, useEffect } from "react";

import type { PersonaDebugPayload } from "~/lib/personalization-debug.server";

import { safeJsonForScript } from "./safe-json";

/**
 * SFCC-11717: SSR inline `<script>` that exposes the personalization debug
 * payload as `window.__PERSONA_TRAITS` for the external MarTech GTM "persona
 * debug" widget to read and render in the browser console / panel. This repo
 * only publishes the data contract — it does NOT render the console output or
 * the visual panel (those live in the GTM container, owned by MarTech).
 * Completes the `window.__PERSONA_TRAITS` injection sub-task originally scoped
 * under SFCC-10723.
 *
 * Gated entirely on `?pers_debug=true`: the loader passes a non-null `payload`
 * only when debug mode is active, and this renders nothing when `payload` is
 * null, so no global is defined on normal traffic.
 *
 * Mirrors PageTypeInlineScript's placement/hydration contract — rendered inside
 * the contiguous inline-script run at the TOP of <body>, nonce'd, and
 * `suppressHydrationWarning`. Placed BEFORE the GTM container script so the
 * global exists before any GTM-fired widget tag reads it. `safeJsonForScript`
 * escapes `<`/`-->` so trait or campaign strings can't break out of the inline
 * script tag.
 */
export const PersonaDebugInlineScript = memo(function PersonaDebugInlineScript({
  payload,
  nonce,
}: {
  payload: PersonaDebugPayload | null;
  nonce?: string;
}) {
  if (!payload) return null;
  return (
    <script
      suppressHydrationWarning
      nonce={nonce}
      dangerouslySetInnerHTML={{
        __html: `window.__PERSONA_TRAITS = ${safeJsonForScript(payload)};`,
      }}
    />
  );
});

/**
 * SFCC-11717: dev-only console echo of the same payload. Lets engineers verify
 * the injected `window.__PERSONA_TRAITS` contract without the external MarTech
 * widget deployed. Deliberately labeled so it is NEVER mistaken for the real
 * widget-rendered `[PERSONALIZATION DEBUG]` output — this is not the production
 * browser-console mechanism, just a development sanity check.
 *
 * Renders nothing (imperative `console.log` from a client effect). The caller
 * gates rendering behind the existing `devConfig` (non-production only); the
 * null-payload guard keeps it silent unless `?pers_debug=true` is also present.
 */
export const PersonaDebugDevEcho = memo(function PersonaDebugDevEcho({
  payload,
}: {
  payload: PersonaDebugPayload | null;
}) {
  useEffect(() => {
    if (!payload) return;
    console.log(
      "[PERSONA DEBUG - DEV ECHO, not the production widget output]",
      payload,
    );
  }, [payload]);
  return null;
});
